/**
 * Explore Universities Tab - Browse and filter universities
 * Reuses existing university browse functionality with tab navigation
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { UniversityFilters } from '../../university/components/UniversityFilters';
import { UniversityList } from '../../university/components/UniversityList';
import { universityAPI } from '../../university/api/university-api';
import { SEO, Header } from '../../../shared/components';
import { ExploreTabs } from '../components';
import type { University, FilterOptions, BrowseFilters } from '../../university/types';

const ExploreUniversitiesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [universities, setUniversities] = useState<University[]>([]);
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Parse filters from URL
  const getFiltersFromURL = (): BrowseFilters => {
    return {
      field: searchParams.get('field') || undefined,
      country: searchParams.get('country') || undefined,
      budgetMin: searchParams.get('budget_min') ? parseInt(searchParams.get('budget_min')!) : undefined,
      budgetMax: searchParams.get('budget_max') ? parseInt(searchParams.get('budget_max')!) : undefined,
      rankingMax: searchParams.get('ranking_max') ? parseInt(searchParams.get('ranking_max')!) : undefined,
      page: searchParams.get('page') ? parseInt(searchParams.get('page')!) : 1,
      sort: searchParams.get('sort') || 'ranking'
    };
  };

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await universityAPI.getFilterOptions();
        setFilterOptions(options);
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    };
    loadFilterOptions();
  }, []);

  // Load universities when filters change
  useEffect(() => {
    const loadUniversities = async () => {
      setIsLoading(true);
      try {
        const filters = getFiltersFromURL();
        const result = await universityAPI.browseUniversities(filters);
        setUniversities(result.universities);
        setPagination(result.pagination);
      } catch (err) {
        console.error('Failed to load universities:', err);
      } finally {
        setIsLoading(false);
      }
    };
    loadUniversities();
  }, [searchParams]);

  // Update URL when filters change
  const handleFilterChange = (filters: BrowseFilters) => {
    const params: any = {};

    if (filters.field) params.field = filters.field;
    if (filters.country) params.country = filters.country;
    if (filters.budgetMin) params.budget_min = filters.budgetMin.toString();
    if (filters.budgetMax) params.budget_max = filters.budgetMax.toString();
    if (filters.rankingMax) params.ranking_max = filters.rankingMax.toString();
    if (filters.sort && filters.sort !== 'ranking') params.sort = filters.sort;
    if (filters.page && filters.page !== 1) params.page = filters.page.toString();

    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const filters = getFiltersFromURL();
    handleFilterChange({ ...filters, page });
  };

  const handleResetFilters = () => {
    setSearchParams({});
  };

  const handleUniversityClick = (slug: string) => {
    navigate(`/university/${slug}`, {
      state: { from: `/explore/universities?${searchParams.toString()}` }
    });
  };

  if (!filterOptions) {
    return (
      <>
        <SEO title="Browse Universities" />
        <Header />
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="flex items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-primary" />
            <span className="text-foreground font-medium">Loading universities...</span>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Explore Universities - 200+ Worldwide"
        description="Browse and compare 200+ universities worldwide. Filter by country, field, budget, and ranking."
      />

      <Header />
      <ExploreTabs />

      <div className="min-h-screen bg-background">
        {/* Main Content with Modern Layout */}
        <div className="container mx-auto px-6 py-10">
          <div className="grid grid-cols-12 gap-8">
            {/* Sticky Sidebar Filters */}
            <div className="col-span-12 lg:col-span-3">
              <div className="sticky top-24">
                <UniversityFilters
                  filters={getFiltersFromURL()}
                  filterOptions={filterOptions}
                  onFilterChange={handleFilterChange}
                  onReset={handleResetFilters}
                />
              </div>
            </div>

            {/* University Results */}
            <div className="col-span-12 lg:col-span-9">
              <UniversityList
                universities={universities}
                pagination={pagination}
                isLoading={isLoading}
                onPageChange={handlePageChange}
                onUniversityClick={handleUniversityClick}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ExploreUniversitiesPage;
